import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Trophy, Users, TrendingUp, Dumbbell, Medal, Crown, 
  UserPlus, Check, X, MessageCircle, Search, Zap
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CommunityPage = () => {
  const [activeTab, setActiveTab] = useState("leaderboard");
  const [leaderboard, setLeaderboard] = useState([]);
  const [myRank, setMyRank] = useState(null);
  const [partners, setPartners] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [requests, setRequests] = useState([]);
  const [leaderboardCategory, setLeaderboardCategory] = useState("overall");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [leaderboardCategory]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [leaderboardRes, myRankRes, partnersRes, suggestionsRes, requestsRes] = await Promise.all([
        axios.get(`${API}/leaderboard?category=${leaderboardCategory}`, { headers }),
        axios.get(`${API}/leaderboard/me`, { headers }),
        axios.get(`${API}/partners`, { headers }),
        axios.get(`${API}/partners/suggestions`, { headers }),
        axios.get(`${API}/partners/requests`, { headers })
      ]);

      setLeaderboard(leaderboardRes.data);
      setMyRank(myRankRes.data);
      setPartners(partnersRes.data);
      setSuggestions(suggestionsRes.data);
      setRequests(requestsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendPartnerRequest = async (targetUserId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/partners/request/${targetUserId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Partner request sent!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send request');
    }
  };

  const respondToRequest = async (requestId, accept) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/partners/requests/${requestId}`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { accept }
      });
      toast.success(accept ? 'Partner added!' : 'Request declined');
      fetchData();
    } catch (error) {
      toast.error('Failed to respond to request');
    }
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-gray-400" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
    return <span className="text-sm font-bold text-muted-foreground">#{rank}</span>;
  };

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl md:text-4xl font-heading font-black text-white tracking-tighter uppercase">
            COMMUNITY
          </h1>
          <p className="text-muted-foreground mt-1">Compete, connect, and grow together</p>
        </div>

        {/* My Rank Card */}
        {myRank && myRank.rank && (
          <Card className="bg-gradient-to-r from-primary/20 to-secondary/20 border-primary/30" data-testid="my-rank-card">
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-primary/30 flex items-center justify-center">
                    <span className="text-2xl font-black text-primary">#{myRank.rank}</span>
                  </div>
                  <div>
                    <p className="text-white font-bold text-lg">Your Global Ranking</p>
                    <p className="text-muted-foreground text-sm">
                      Score: {myRank.overall_score?.toLocaleString()} pts
                    </p>
                  </div>
                </div>
                <div className="hidden md:flex gap-6 text-center">
                  <div>
                    <p className="text-2xl font-black text-white">{myRank.workouts}</p>
                    <p className="text-xs text-muted-foreground">Workouts</p>
                  </div>
                  <div>
                    <p className="text-2xl font-black text-green-500">+{myRank.vertical_gain}"</p>
                    <p className="text-xs text-muted-foreground">Vertical Gain</p>
                  </div>
                  <div>
                    <p className="text-2xl font-black text-yellow-500">{myRank.badges}</p>
                    <p className="text-xs text-muted-foreground">Badges</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-3 w-full md:w-auto">
            <TabsTrigger value="leaderboard" data-testid="tab-leaderboard">
              <Trophy className="w-4 h-4 mr-2" />
              Leaderboard
            </TabsTrigger>
            <TabsTrigger value="partners" data-testid="tab-partners">
              <Users className="w-4 h-4 mr-2" />
              Partners
            </TabsTrigger>
            <TabsTrigger value="find" data-testid="tab-find">
              <Search className="w-4 h-4 mr-2" />
              Find
            </TabsTrigger>
          </TabsList>

          {/* Leaderboard Tab */}
          <TabsContent value="leaderboard" className="space-y-4">
            {/* Category Filter */}
            <div className="flex gap-2 flex-wrap">
              {[
                { id: "overall", label: "Overall", icon: Zap },
                { id: "vertical", label: "Vertical", icon: TrendingUp },
                { id: "workouts", label: "Workouts", icon: Dumbbell },
                { id: "bodyweight", label: "Bodyweight", icon: Users }
              ].map((cat) => (
                <Button
                  key={cat.id}
                  variant={leaderboardCategory === cat.id ? "default" : "outline"}
                  size="sm"
                  onClick={() => setLeaderboardCategory(cat.id)}
                  className={leaderboardCategory === cat.id ? "bg-primary" : ""}
                >
                  <cat.icon className="w-4 h-4 mr-1" />
                  {cat.label}
                </Button>
              ))}
            </div>

            {/* Leaderboard List */}
            <Card className="bg-card border-border" data-testid="leaderboard-card">
              <CardContent className="p-0">
                {leaderboard.length > 0 ? (
                  <div className="divide-y divide-border">
                    {leaderboard.map((entry, index) => (
                      <div 
                        key={entry.user_id}
                        className={`flex items-center justify-between p-4 ${index < 3 ? 'bg-primary/5' : ''}`}
                        data-testid={`leaderboard-entry-${index}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 flex items-center justify-center">
                            {getRankBadge(entry.rank)}
                          </div>
                          <div>
                            <p className="font-bold text-white">{entry.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {entry.workouts} workouts • +{entry.vertical_gain}" vertical
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-primary">{entry.overall_score?.toLocaleString()}</p>
                          <p className="text-xs text-muted-foreground">points</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center text-muted-foreground">
                    <Trophy className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No rankings yet. Start training to climb the leaderboard!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Partners Tab */}
          <TabsContent value="partners" className="space-y-4">
            {/* Partner Requests */}
            {requests.length > 0 && (
              <Card className="bg-card border-yellow-500/30" data-testid="partner-requests">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <UserPlus className="w-5 h-5 text-yellow-500" />
                    Partner Requests ({requests.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {requests.map((req) => (
                    <div key={req.id} className="flex items-center justify-between p-3 bg-background rounded-sm">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                          <Users className="w-5 h-5 text-primary" />
                        </div>
                        <p className="font-medium text-white">{req.from_user?.name}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => respondToRequest(req.id, true)} className="bg-green-600 hover:bg-green-700">
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => respondToRequest(req.id, false)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Current Partners */}
            <Card className="bg-card border-border" data-testid="my-partners">
              <CardHeader>
                <CardTitle className="text-lg">My Training Partners</CardTitle>
                <CardDescription>Athletes you're connected with</CardDescription>
              </CardHeader>
              <CardContent>
                {partners.length > 0 ? (
                  <div className="grid gap-3">
                    {partners.map((partner) => (
                      <div key={partner.user_id} className="flex items-center justify-between p-3 bg-background rounded-sm">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                            <Users className="w-6 h-6 text-green-500" />
                          </div>
                          <div>
                            <p className="font-bold text-white">{partner.name}</p>
                            <p className="text-xs text-muted-foreground">
                              Partners since {new Date(partner.since).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <Button size="sm" variant="outline">
                          <MessageCircle className="w-4 h-4 mr-1" />
                          Message
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No training partners yet</p>
                    <p className="text-sm mt-1">Find athletes to train with!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Find Partners Tab */}
          <TabsContent value="find" className="space-y-4">
            <Card className="bg-card border-border" data-testid="partner-suggestions">
              <CardHeader>
                <CardTitle className="text-lg">Suggested Training Partners</CardTitle>
                <CardDescription>Athletes at similar levels to train with</CardDescription>
              </CardHeader>
              <CardContent>
                {suggestions.length > 0 ? (
                  <div className="grid gap-3">
                    {suggestions.map((user) => (
                      <div key={user.user_id} className="flex items-center justify-between p-4 bg-background rounded-sm border border-border">
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center">
                            <Users className="w-7 h-7 text-primary" />
                          </div>
                          <div>
                            <p className="font-bold text-white">{user.name}</p>
                            <div className="flex items-center gap-3 mt-1">
                              <span className="text-xs text-muted-foreground">
                                {user.current_vertical}" vertical
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {user.workouts} workouts
                              </span>
                            </div>
                            <Badge variant="outline" className="mt-2 text-xs border-green-500/30 text-green-500">
                              {user.compatibility}% match
                            </Badge>
                          </div>
                        </div>
                        <Button onClick={() => sendPartnerRequest(user.user_id)} className="bg-primary hover:bg-primary/90">
                          <UserPlus className="w-4 h-4 mr-1" />
                          Connect
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No suggestions available</p>
                    <p className="text-sm mt-1">More athletes will appear as the community grows!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default CommunityPage;
